import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import * as cognitoAuth from '../utils/cognitoAuth';
import { fetchReportPDF as fetchReportPDFUtil } from '../utils/api';

function isTokenExpired(token) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= exp * 1000 - 30000; // 30s buffer
  } catch {
    return true;
  }
}

export function useApi() {
  const { idToken, refreshToken: storedRefreshToken, setIdToken, isPlatformSso, logout } = useAuth();

  const fetchAPI = useCallback(async (endpoint, options = {}) => {
    const { responseType = 'json', ...fetchOptions } = options;

    // Fetch a fresh token and update state; calls logout+throws on failure.
    const refreshAndStore = () => (
      isPlatformSso && storedRefreshToken
        ? cognitoAuth.refreshPlatformSession(storedRefreshToken)
        : cognitoAuth.getCurrentSession()
    ).then(
      ({ tokens }) => { setIdToken(tokens.idToken); sessionStorage.setItem('idToken', tokens.idToken); return tokens.idToken; },
      () => { logout(); throw new Error('Session expired. Please log in again.'); }
    );

    let currentToken = idToken;
    if (!currentToken || isTokenExpired(currentToken)) {
      currentToken = await refreshAndStore();
    }

    const makeRequest = (token) =>
      fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...fetchOptions.headers,
        },
      });

    let response = await makeRequest(currentToken);

    if (response.status === 401) {
      currentToken = await refreshAndStore();
      response = await makeRequest(currentToken);
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const error = new Error(err.message || err.error || `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }

    if (responseType === 'response') return response;
    if (responseType === 'text') return response.text();
    if (responseType === 'blob') return response.blob();
    return response.json();
  }, [idToken, storedRefreshToken, isPlatformSso, setIdToken, logout]);

  const fetchDocuments = useCallback((status = null) => {
    const endpoint = status
      ? `/api/documents?status=${encodeURIComponent(status)}`
      : '/api/documents';
    return fetchAPI(endpoint).then((r) => r.documents || []);
  }, [fetchAPI]);

  const fetchReports = useCallback(async (status = null, limit = 100) => {
    const effectiveLimit = Math.min(limit, 100);
    let nextToken = null;
    let pageCount = 0;
    const allRuns = [];
    do {
      let endpoint = `/api/jobs?limit=${effectiveLimit}`;
      if (status) endpoint += `&status=${encodeURIComponent(status)}`;
      if (nextToken) endpoint += `&next_token=${encodeURIComponent(nextToken)}`;
      const response = await fetchAPI(endpoint);
      allRuns.push(...(response.runs || []));
      nextToken = response.next_token || null;
      pageCount += 1;
    } while (nextToken && pageCount < 20);
    return allRuns;
  }, [fetchAPI]);

  const fetchJobStatus = useCallback(
    (runId) => fetchAPI(`/api/jobs/${runId}/status`),
    [fetchAPI]
  );

  const fetchRunStatus = useCallback(
    (runId) => fetchAPI(`/api/runs/${runId}/status`),
    [fetchAPI]
  );

  const deleteReport = useCallback(
    (runId) => fetchAPI(`/api/jobs/delete/${runId}`, { method: 'DELETE' }),
    [fetchAPI]
  );

  const generateReport = useCallback((primaryPolicyId = null, analysisModelId = null) => {
    const orgId = process.env.REACT_APP_ORG_ID;
    if (!orgId) {
      throw new Error('Organization ID not configured. Please set REACT_APP_ORG_ID in your environment file.');
    }
    const body = { org_id: orgId };
    if (primaryPolicyId) body.primary_policy_id = primaryPolicyId;
    if (analysisModelId) body.analysis_model_id = analysisModelId;
    return fetchAPI('/api/analyze', { method: 'POST', body: JSON.stringify(body) });
  }, [fetchAPI]);

  const fetchActiveOperations = useCallback(
    () => fetchAPI('/api/operations'),
    [fetchAPI]
  );

  const submitBugReport = useCallback(
    ({ title, description, reporterEmail, reporterName }) =>
      fetchAPI('/api/bug-reports', {
        method: 'POST',
        body: JSON.stringify({ title, description, reporterEmail, reporterName }),
      }),
    [fetchAPI]
  );

  const fetchReportPDF = useCallback(
    (...args) => fetchReportPDFUtil(fetchAPI, ...args),
    [fetchAPI]
  );

  const triggerScan = useCallback((policyIdA, policyIdsB, filenameA, filenamesB, options = {}) => {
    // Self-scan omits policy_id_b/policy_ids_b entirely — backend distinguishes by comparison_type.
    const body = options.comparisonType === 'self'
      ? { policy_id_a: policyIdA, filename_a: filenameA, comparison_type: 'self' }
      : {
          policy_id_a: policyIdA,
          policy_ids_b: Array.isArray(policyIdsB) ? policyIdsB : [policyIdsB],
          filename_a: filenameA,
          filenames_b: Array.isArray(filenamesB) ? filenamesB : [filenamesB],
        };
    return fetchAPI('/api/scan', { method: 'POST', body: JSON.stringify(body) });
  }, [fetchAPI]);

  const fetchFindings = useCallback(
    (runId) => fetchAPI(`/api/runs/${runId}/findings`),
    [fetchAPI]
  );

  const cancelRun = useCallback(
    (runId) => fetchAPI(`/api/runs/${runId}`, { method: 'DELETE' }),
    [fetchAPI]
  );

  const triggerLibraryScan = useCallback((docs) =>
    fetchAPI('/api/library-scan', {
      method: 'POST',
      body: JSON.stringify({ docs }),
    }), [fetchAPI]);

  // Fetch HTML report as authenticated blob URL — avoids exposing the JWT in the URL.
  const openHtmlReport = useCallback(async (runId) => {
    const html = await fetchAPI(`/api/reports/${runId}/html`, { responseType: 'text' });
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    // Revoke after a short delay — gives the new tab time to load before the reference is dropped.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }, [fetchAPI]);

  return {
    fetchAPI,
    fetchDocuments,
    fetchReports,
    fetchJobStatus,
    fetchRunStatus,
    deleteReport,
    generateReport,
    fetchActiveOperations,
    submitBugReport,
    fetchReportPDF,
    triggerScan,
    fetchFindings,
    cancelRun,
    triggerLibraryScan,
    openHtmlReport,
  };
}
